import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a free shipping progress bar.
 * Listens for cart updates and updates the progress bar accordingly.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} progressBar - The progress bar fill element.
 * @property {HTMLElement} progressText - The progress text element.
 * @property {HTMLElement} successMessage - The success message element.
 *
 * @extends {Component<Refs>}
 */
class FreeShippingProgressComponent extends Component {
  /** @type {number} */
  #threshold = 0;

  /** @type {string} */
  #progressMessage = '';

  /** @type {string} */
  #successMessage = '';

  /** @type {string} */
  #moneyFormat = '';

  connectedCallback() {
    super.connectedCallback();

    this.#threshold = parseInt(this.dataset.threshold || '7000', 10);
    this.#progressMessage = this.dataset.progressMessage || "You're AMOUNT_PLACEHOLDER away from free shipping!";
    this.#successMessage = this.dataset.successMessage || 'Free shipping unlocked!';
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);

    // [FROST BUDDY] Initialize from the server-rendered cart total passed in via
    // data-initial-total instead of fetching /cart.js on every page load. The
    // total is already known at render time; the fetch was an avoidable
    // round-trip firing site-wide. Fall back to the fetch only if the attribute
    // is missing/unparseable. Live changes still come through cartLinesUpdate.
    const initialTotal = parseInt(this.dataset.initialTotal ?? '', 10);
    if (Number.isFinite(initialTotal)) {
      this.#updateProgressWithTotal(initialTotal);
    } else {
      this.#updateProgress();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events (v4.0.0 @shopify/events CartLinesUpdateEvent).
   * The event exposes a promise that resolves to { cart, detail } where
   * cart.cost.totalAmount.amount is a string ("29.99"). Convert to cents to
   * match the existing #updateProgressWithTotal API.
   * @param {Event & { promise?: Promise<{ cart?: { cost?: { totalAmount?: { amount?: string } } } }> }} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const amountStr = cart?.cost?.totalAmount?.amount;
        if (amountStr !== undefined) {
          const cents = Math.round(parseFloat(amountStr) * 100);
          this.#updateProgressWithTotal(cents);
        } else {
          this.#updateProgress();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[free-shipping-progress] cart update promise rejected:', error);
        }
        this.#updateProgress();
      });
  };

  /**
   * Updates the progress bar by fetching current cart data.
   */
  async #updateProgress() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#updateProgressWithTotal(cart.total_price);
    } catch (error) {
      console.error('Failed to fetch cart data:', error);
    }
  }

  /**
   * Updates the progress bar based on cart total.
   * @param {number} totalPrice - The cart total in cents.
   */
  #updateProgressWithTotal(totalPrice) {
    const { progressBar, progressText, successMessage } = this.refs;

    if (!progressBar || !progressText || !successMessage) return;

    const percentage = Math.min((totalPrice / this.#threshold) * 100, 100);
    const remaining = Math.max(this.#threshold - totalPrice, 0);
    const hasQualified = totalPrice >= this.#threshold;

    // Update progress bar width
    progressBar.style.setProperty('--progress-width', `${percentage}%`);

    // Update ARIA attributes for accessibility
    progressBar.setAttribute('aria-valuenow', String(Math.round(percentage)));

    // Toggle visibility of messages
    if (hasQualified) {
      progressText.classList.add('hidden');
      successMessage.classList.remove('hidden');
      this.classList.add('free-shipping-progress--qualified');
    } else {
      progressText.classList.remove('hidden');
      successMessage.classList.add('hidden');
      this.classList.remove('free-shipping-progress--qualified');

      // Update the remaining amount text
      const formattedAmount = this.#formatMoney(remaining);
      progressText.textContent = this.#progressMessage.replace('AMOUNT_PLACEHOLDER', formattedAmount);
    }
  }

  /**
   * Formats a price in cents to a currency string.
   * @param {number} cents - The price in cents.
   * @returns {string} The formatted price string.
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountWithoutDecimals = (cents / 100).toFixed(0);

    // Use the money format from Shopify, supporting common formats
    return this.#moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountWithoutDecimals)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }
}

if (!customElements.get('free-shipping-progress-component')) {
  customElements.define('free-shipping-progress-component', FreeShippingProgressComponent);
}
